import type { ReactNode } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import { Typography } from '@/components/ui/typography'
import dayjs from '@/lib/dayjs'
import type { CashDrawerReportSession } from '@/lib/services/cash-drawers/cash-drawers.types'
import type { CashDrawerReportSummary } from '@/lib/services/reports/reports.types'
import { formatCurrency, parseCurrency, type NumericInput } from '@/lib/utils'
import {
  Bar,
  BarChart,
  Rectangle,
  ReferenceLine,
  XAxis,
  YAxis,
  type RectangleProps,
} from 'recharts'
import {
  buildCloseChartData,
  formatCashChartDate,
  getCloseBalance,
  formatCashDrawerDifference,
  cashDrawerDifferenceTone,
  type CloseChartRow,
} from './cash-drawer-report.presentation'

type SelectSession = (session: CashDrawerReportSession) => void
type CashDrawerChartRow = CloseChartRow<CashDrawerReportSession>

const closeChartConfig: ChartConfig = {
  cash: { label: 'Cash', color: 'var(--color-status-warning-foreground)' },
  checks: { label: 'Checks', color: 'var(--color-status-destructive-foreground)' },
}

type CashDrawerSummaryCardsProps = {
  /** Undefined while the report loads. */
  summary: CashDrawerReportSummary | undefined
  sessions?: CashDrawerReportSession[]
}

export function CashDrawerSummaryCards({ summary, sessions = [] }: CashDrawerSummaryCardsProps) {
  const cash = summary?.payment_methods.cash
  const checks = summary?.payment_methods.check
  const closes = sessions.filter((session) => session.closed_at)
  const different = closes.filter((session) => getCloseBalance(session) === 'different').length
  const unknown = closes.filter((session) => getCloseBalance(session) === 'unknown').length
  const money = (value: NumericInput) => (summary ? formatCurrency(value ?? 0) : '—')
  return (
    <div className="@container">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,190px),1fr))] gap-3.5">
        <SummaryCard
          title="Cash and checks taken in"
          value={money(parseCurrency(cash?.collected) + parseCurrency(checks?.collected))}
        >
          Cash and checks into the drawer
          <Typography variant="caption" className="mt-1 block">
            Cash {money(cash?.collected)} · Checks {money(checks?.collected)}
          </Typography>
        </SummaryCard>
        <SummaryCard
          title="Cash and check refunds"
          value={money(-(parseCurrency(cash?.refunded) + parseCurrency(checks?.refunded)))}
        >
          Handed back: cash {money(cash?.refunded)} · checks {money(checks?.refunded)}
          {parseCurrency(cash?.reversed) !== 0 || parseCurrency(checks?.reversed) !== 0 ? (
            <Typography variant="caption" className="mt-1 block">
              Refunds reversed: cash {money(cash?.reversed)} · checks {money(checks?.reversed)}
            </Typography>
          ) : null}
        </SummaryCard>
        <SummaryCard title="Cash paid out" value={money(-parseCurrency(summary?.adjustments.out))}>
          Logged from the drawer · Cash added {money(summary?.adjustments.in)}
        </SummaryCard>
        <SummaryCard title="Over or short">
          <Typography
            variant="h4"
            tone={cashDrawerDifferenceTone(summary?.discrepancies.cash)}
            className="block tabular-nums"
          >
            Cash {formatCashDrawerDifference(summary?.discrepancies.cash)}
          </Typography>
          <Typography
            variant="h4"
            tone={cashDrawerDifferenceTone(summary?.discrepancies.checks)}
            className="block tabular-nums"
          >
            Checks {formatCashDrawerDifference(summary?.discrepancies.checks)}
          </Typography>
          <Typography variant="caption" className="mt-2 block">
            {summary
              ? closes.length && !different && !unknown
                ? 'Every count balanced'
                : `${different} of ${closes.length} counts with a difference${unknown ? ` · ${unknown} missing counts` : ''}`
              : 'Loading count totals…'}
          </Typography>
        </SummaryCard>
      </div>
    </div>
  )
}

type SummaryCardProps = {
  title: string
  value?: string
  children: ReactNode
}

function SummaryCard({ title, value, children }: SummaryCardProps) {
  return (
    <Card role="group" aria-label={`${title} summary`} className="gap-2 p-4">
      <Typography variant="caption">{title}</Typography>
      {value != null ? (
        <Typography variant="h3" className="tabular-nums">
          {value}
        </Typography>
      ) : null}
      <Typography as="div" variant="caption">
        {children}
      </Typography>
    </Card>
  )
}

// Recharts hands chart rows back to its callbacks and injected props typed as `any`; read them as
// unknown and match them to the typed rows by id.
function findChartRow(rows: CashDrawerChartRow[], entry: unknown) {
  if (typeof entry !== 'object' || entry === null || !('id' in entry)) return undefined
  return rows.find((row) => row.id === entry.id)
}

type CashDrawerChartsProps = {
  sessions?: CashDrawerReportSession[]
  timezone: string
  onSelectSession?: SelectSession
}

export function CashDrawerCharts({
  sessions = [],
  timezone,
  onSelectSession,
}: CashDrawerChartsProps) {
  const chartData = buildCloseChartData(sessions)
  const limit = Math.max(
    1,
    ...chartData.flatMap((row) => [Math.abs(row.cash ?? 0), Math.abs(row.checks ?? 0)])
  )
  const selectChartRow = (entry: unknown) => {
    const row = findChartRow(chartData, entry)
    if (row) onSelectSession?.(row)
  }
  return (
    <Card size="sm">
      <CardHeader size="sm">
        <CardTitle size="xs">Over or short, by count</CardTitle>
        <CardDescription>
          Over above zero, short below. Pick a bar or a count below to see it.
        </CardDescription>
      </CardHeader>
      <CardContent size="sm">
        {chartData.length ? (
          <ChartContainer
            config={closeChartConfig}
            className="h-44 w-full"
            aria-label="Cash and check differences by count"
          >
            <BarChart data={chartData} accessibilityLayer>
              <XAxis
                axisLine={false}
                tickLine={false}
                dataKey="id"
                tickFormatter={(id: unknown) => {
                  const row = chartData.find((candidate) => candidate.id === id)
                  return row ? formatCashChartDate(row.closed_at, timezone) : ''
                }}
              />
              <YAxis hide domain={[-limit, limit]} />
              <ReferenceLine
                y={0}
                stroke="var(--color-border)"
                strokeDasharray="3 3"
                pointerEvents="none"
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    hideIndicator
                    labelFormatter={(_, payload) => {
                      const row = findChartRow(chartData, payload[0]?.payload)
                      return row
                        ? `${dayjs(row.closed_at).tz(timezone).format('MMM D, h:mm A')} · ${row.workstation_name}`
                        : ''
                    }}
                    formatter={(value, name) => (
                      <span>
                        {closeChartConfig[name]?.label ?? name}: {formatCurrency(value)}
                      </span>
                    )}
                  />
                }
              />
              <Bar
                dataKey="cash"
                isAnimationActive={false}
                fill="var(--color-cash)"
                maxBarSize={26}
                shape={<CloseVarianceBar />}
                background={
                  <CloseCountTarget
                    rows={chartData}
                    timezone={timezone}
                    tender="Cash"
                    onSelectSession={onSelectSession}
                  />
                }
                onClick={(bar) => selectChartRow(bar.payload)}
              />
              <Bar
                dataKey="checks"
                isAnimationActive={false}
                fill="var(--color-checks)"
                maxBarSize={26}
                shape={<CloseVarianceBar />}
                background={
                  <CloseCountTarget
                    rows={chartData}
                    timezone={timezone}
                    tender="Checks"
                    onSelectSession={onSelectSession}
                  />
                }
                onClick={(bar) => selectChartRow(bar.payload)}
              />
            </BarChart>
          </ChartContainer>
        ) : (
          <Typography variant="supporting">No counts in this range.</Typography>
        )}
        <Typography variant="caption" className="mt-2 block">
          Cash on the left; checks on the right. Differences can offset, so review both counts.
        </Typography>
      </CardContent>
    </Card>
  )
}

// Recharts clones the `shape` element with the bar's geometry and value.
type CloseVarianceBarProps = RectangleProps & {
  value?: number | [number, number]
}

function CloseVarianceBar({ value, ...props }: CloseVarianceBarProps) {
  return (
    <Rectangle
      {...props}
      fill={
        typeof value === 'number' && value < 0
          ? 'var(--color-status-destructive-foreground)'
          : 'var(--color-status-warning-foreground)'
      }
    />
  )
}

type CloseCountTargetProps = {
  // Recharts clones the `background` element with the background box and the bar's data row.
  x?: number
  y?: number
  width?: number
  height?: number
  payload?: unknown
  rows: CashDrawerChartRow[]
  timezone: string
  tender: string
  onSelectSession?: SelectSession
}

// A full-height target keeps zero/short bars selectable without changing their actual values.
function CloseCountTarget({
  x,
  y,
  width,
  height,
  payload,
  rows,
  timezone,
  tender,
  onSelectSession,
}: CloseCountTargetProps) {
  const row = findChartRow(rows, payload)
  if (!row) return null
  return (
    <g
      role="button"
      tabIndex={0}
      aria-label={`View ${tender.toLowerCase()} count for ${dayjs(row.closed_at).tz(timezone).format('MMM D, h:mm A')} · ${row.workstation_name}`}
      onClick={() => onSelectSession?.(row)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onSelectSession?.(row)
        }
      }}
      className="cursor-pointer"
    >
      <Rectangle x={x} y={y} width={width} height={height} fill="transparent" />
    </g>
  )
}
